import { createFileRoute, Link } from "@tanstack/react-router";
import { fetchStrapiAPI } from "@/services/strapi";
import ScrollAnimation from "@/components/Home/Animations";
import Navbar from "@/components/Home/NavBar";
import Footer from "@/components/Home/Footer";
import Markdown from "react-markdown";

interface BlogPost {
  id: number;
  documentId: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  publishedDate: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  featuredImage?: {
    data: {
      attributes: {
        url: string;
        alternativeText?: string;
      };
    } | null;
  };
  author?: {
    data: {
      attributes: {
        name: string;
        picture?: {
          data: {
            attributes: {
              url: string;
            };
          };
        };
      };
    } | null;
  };
}

interface BlogPostResponse {
  data: BlogPost[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export const Route = createFileRoute("/blog/$slug")({
  component: BlogPostPage,
  head: ({ loaderData }) => {
    if (!loaderData || !("blogPost" in loaderData)) {
      return {
        meta: [
          {
            title: "Article Not Found | aiEmailAgent Blog",
          },
          {
            description: "Article Not Found",
          },
        ],
      };
    }

    const blogPost = loaderData["blogPost"] as BlogPost;
    // Create a clean excerpt for meta description
    const metaDescription =
      blogPost.excerpt ||
      blogPost.content.replace(/[*#]/g, "").slice(0, 160).trim() + "...";

    // Get the featured image URL for social sharing
    const ogImage =
      blogPost.featuredImage?.data?.attributes?.url || "/og-image.jpg";
    return {
      meta: [
        {
          title: `${blogPost?.title} | aiEmailAgent Blog`,
        },
        {
          description: metaDescription,
        },
        {
          image: ogImage,
        },
      ],
    };
  },

  loader: async ({ params }) => {
    try {
      const data = await fetchStrapiAPI<BlogPostResponse>(
        `/blog-posts?filters[slug][$eq]=${params.slug}&pLevel`,
      );
      if (!data?.data?.[0]) {
        throw new Error("Blog post not found");
      }
      return { blogPost: data.data[0] as BlogPost };
    } catch (error) {
      console.error("Error fetching blog post:", error);
      return { blogPost: null };
    }
  },
});

function BlogPostPage() {
  const { blogPost } = Route.useLoaderData() as { blogPost: BlogPost };

  if (!blogPost) {
    return (
      <ScrollAnimation>
        <Navbar />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Blog Post Not Found</h1>
            <Link to="/blog" className="text-primary hover:underline">
              Return to Blog
            </Link>
          </div>
        </div>
        <Footer />
      </ScrollAnimation>
    );
  }

  const formattedDate = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(blogPost.publishedDate));

  return (
    <ScrollAnimation>
      <Navbar />
      <div className="min-h-screen bg-background py-20">
        <div className="max-w-4xl mx-auto px-6">
          {/* Navigation */}
          <div className="mb-16">
            <Link
              to="/blog"
              className="text-primary hover:underline inline-flex items-center gap-2 text-lg"
            >
              ← Back to Blog
            </Link>
          </div>

          {/* Article Header */}
          <header className="mb-16">
            {blogPost.featuredImage?.data?.attributes?.url && (
              <div className="aspect-video w-full overflow-hidden rounded-xl mb-12">
                <img
                  src={blogPost.featuredImage.data.attributes.url}
                  alt={
                    blogPost.featuredImage.data.attributes.alternativeText ||
                    blogPost.title
                  }
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8">
              {blogPost.title}
            </h1>

            <div className="flex items-center gap-8 text-muted-foreground">
              {blogPost.author?.data?.attributes && (
                <div className="flex items-center gap-4">
                  {blogPost.author.data.attributes.picture?.data?.attributes
                    ?.url ? (
                    <img
                      src={
                        blogPost.author.data.attributes.picture.data.attributes
                          .url
                      }
                      alt={blogPost.author.data.attributes.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-base font-medium">
                        {blogPost.author.data.attributes.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <span className="font-medium text-lg">
                    {blogPost.author.data.attributes.name}
                  </span>
                </div>
              )}
              <div className="text-base">{formattedDate}</div>
            </div>
          </header>

          {/* Article Content */}
          <article className="prose prose-lg dark:prose-invert max-w-none prose-headings:mb-8 prose-p:mb-6 prose-img:my-12 prose-blockquote:my-12">
            <Markdown>{blogPost.content}</Markdown>
          </article>
        </div>
      </div>
      <Footer />
    </ScrollAnimation>
  );
}
